import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowLeftIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CalendarIcon,
  UserGroupIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationCircleIcon,
  InformationCircleIcon,
  ChatBubbleLeftRightIcon,
  ArrowPathIcon,
  EyeIcon,
} from '@heroicons/react/24/outline';
import Layout from '../../components/layout/Layout';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import TaskModal from '../../components/admin/TaskModal';
import TaskDetailsModal from '../../components/admin/TaskDetailsModal';
import RecurringTaskModal from '../../components/admin/RecurringTaskModal';
import RecurringTaskDetailsModal from '../../components/admin/RecurringTaskDetailsModal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { projectsAPI, tasksAPI, teamsAPI, usersAPI, authAPI } from '../../services/api';
import toast from 'react-hot-toast';

interface Project {
  id: string;
  name: string;
  description?: string;
  status: string;
  priority: string;
  startDate?: string;
  endDate?: string;
  dueDate?: string;
  budget?: number;
  color: string;
  isArchived: boolean;
  createdAt: string;
  updatedAt: string;
  creator: any;
  teams?: { team: { id: string; name: string; color: string } }[];
  tasks: any[];
  _count: { tasks: number };
}

const ProjectDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  
  const [project, setProject] = useState<Project | null>(null);
  const [tasks, setTasks] = useState<any[]>([]);
  const [recurringTasks, setRecurringTasks] = useState<any[]>([]);
  const [teams, setTeams] = useState<any[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isTaskModalOpen, setIsTaskModalOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<any | null>(null);
  const [taskToDelete, setTaskToDelete] = useState<any | null>(null);
  const [isTaskDetailsModalOpen, setIsTaskDetailsModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState<any | null>(null);
  const [isRecurringTaskModalOpen, setIsRecurringTaskModalOpen] = useState(false);
  const [editingRecurringTask, setEditingRecurringTask] = useState<any | null>(null);
  const [isRecurringTaskDetailsModalOpen, setIsRecurringTaskDetailsModalOpen] = useState(false);
  const [selectedRecurringTask, setSelectedRecurringTask] = useState<any | null>(null);

  useEffect(() => {
    if (id) {
      loadProjectDetails();
    }
  }, [id]);

  const loadProjectDetails = async () => {
    try {
      setIsLoading(true);
      const [projectData, projectTasks, projectRecurringTasks, teamsData, usersData, userData] = await Promise.all([
        projectsAPI.getById(id!),
        projectsAPI.getProjectTasks(id!),
        projectsAPI.getProjectRecurringTasks(id!),
        teamsAPI.getAll(),
        usersAPI.getAll(),
        authAPI.getCurrentUser(),
      ]);

      setProject(projectData);
      setTasks(projectTasks);
      setRecurringTasks(projectRecurringTasks);
      setTeams(teamsData);
      setUsers(usersData);
      setCurrentUser(userData);
    } catch (error) {
      console.error('Error loading project details:', error);
      toast.error('Failed to load project details');
    } finally {
      setIsLoading(false);
    }
  };

  const handleAddTask = async (taskData: any) => {
    try {
      if (!taskData.isRecurring) {
        // For regular tasks, use the project API and ensure creatorId is included
        const taskDataWithCreator = {
          ...taskData,
          projectId: id,
          creatorId: currentUser?.id || 'cmdd8jvdu0001v1hsfzjmmox6' // Fallback
        };
        await projectsAPI.addTaskToProject(id!, taskDataWithCreator);
        toast.success('Task created successfully');
      } else {
        // For recurring tasks, use the regular tasks API with project assignment
        const recurringTaskDataWithProject = {
          ...taskData,
          recurringTaskData: {
            ...taskData.recurringTaskData,
            projectId: id,
          }
        };
        await tasksAPI.create(recurringTaskDataWithProject);
        toast.success('Recurring task created successfully');
      }

      loadProjectDetails();
      setIsTaskModalOpen(false);
      setEditingTask(null);
    } catch (error) {
      console.error('Error creating task:', error);
      toast.error('Failed to create task');
    }
  };

  const handleEditTask = async (taskData: any) => {
    try {
      await tasksAPI.update(editingTask.id, taskData);
      toast.success('Task updated successfully');
      loadProjectDetails();
      setIsTaskModalOpen(false);
      setEditingTask(null);
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Failed to update task');
    }
  };

  const handleDeleteTask = async () => {
    if (!taskToDelete) return;
    
    try {
      await tasksAPI.delete(taskToDelete.id);
      toast.success('Task deleted successfully');
      loadProjectDetails();
      setTaskToDelete(null);
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error('Failed to delete task');
    }
  };

  const handleEditRecurringTask = (recurringTask: any) => {
    setEditingRecurringTask(recurringTask);
    setIsRecurringTaskModalOpen(true);
  };

  const handleDeleteRecurringTask = async (recurringTask: any) => {
    if (!window.confirm(`Are you sure you want to delete the recurring task "${recurringTask.title}"?`)) {
      return;
    }

    try {
      await tasksAPI.deleteRecurringTask(recurringTask.id);
      toast.success('Recurring task deleted successfully');
      loadProjectDetails(); // Reload project details
    } catch (error) {
      console.error('Error deleting recurring task:', error);
      toast.error('Failed to delete recurring task');
    }
  };

  const handleViewRecurringTaskDetails = (recurringTask: any) => {
    setSelectedRecurringTask(recurringTask);
    setIsRecurringTaskDetailsModalOpen(true);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'TODO': return 'bg-gray-100 text-gray-800';
      case 'IN_PROGRESS': return 'bg-blue-100 text-blue-800';
      case 'REVIEW': return 'bg-yellow-100 text-yellow-800';
      case 'DONE': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'HIGH': return <ExclamationCircleIcon className="w-4 h-4 text-red-500" />;
      case 'NORMAL': return <InformationCircleIcon className="w-4 h-4 text-blue-500" />;
      case 'LOW': return <CheckCircleIcon className="w-4 h-4 text-green-500" />;
      default: return <InformationCircleIcon className="w-4 h-4 text-gray-500" />;
    }
  };

  if (isLoading) {
    return (
      <Layout>
        <LoadingSpinner />
      </Layout>
    );
  }

  if (!project) {
    return (
      <Layout>
        <div className="text-center py-12">
          <h2 className="text-2xl font-bold text-gray-900">Project not found</h2>
          <button
            onClick={() => navigate('/admin/projects')}
            className="mt-4 btn-primary"
          >
            Back to Projects
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/admin/projects')}
              className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
            >
              <ArrowLeftIcon className="w-5 h-5" />
            </button>
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{project.name}</h1>
              <p className="text-sm text-gray-500">
                Created {new Date(project.createdAt).toLocaleDateString()}
              </p>
            </div>
          </div>
          
          <button
            onClick={() => {
              setEditingTask(null);
              setIsTaskModalOpen(true);
            }}
            className="flex items-center space-x-2 px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700 transition-colors"
          >
            <PlusIcon className="w-4 h-4" />
            <span>Add Task</span>
          </button>
        </div>

        {/* Project Info */}
        <div className="bg-white rounded-lg shadow p-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">Status</h3>
              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(project.status)}`}>
                {project.status}
              </span>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">Priority</h3>
              <div className="flex items-center space-x-2">
                {getPriorityIcon(project.priority)}
                <span className="text-sm text-gray-900">{project.priority}</span>
              </div>
            </div>
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-2">Teams</h3>
              <div className="flex flex-wrap gap-1">
                {project.teams?.map((pt) => (
                  <span
                    key={pt.team.id}
                    className="inline-flex px-2 py-1 text-xs font-medium rounded-full"
                    style={{ backgroundColor: pt.team.color + '20', color: pt.team.color }}
                  >
                    {pt.team.name}
                  </span>
                ))}
              </div>
            </div>
          </div>
          
          {project.description && (
            <div className="mt-6">
              <h3 className="text-sm font-medium text-gray-500 mb-2">Description</h3>
              <p className="text-gray-900">{project.description}</p>
            </div>
          )}
        </div>

        {/* Tasks */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">
              Tasks ({tasks.length})
            </h2>
          </div>
          
          <div className="divide-y divide-gray-200">
            {tasks.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                No tasks yet. Add your first task to get started.
              </div>
            ) : (
              tasks.map((task) => (
                <motion.div
                  key={task.id}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  className="p-6 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <h3 className="text-sm font-medium text-gray-900">{task.title}</h3>
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(task.status)}`}>
                          {task.status}
                        </span>
                        {getPriorityIcon(task.priority)}
                      </div>
                      {task.description && (
                        <p className="mt-1 text-sm text-gray-500 line-clamp-2">{task.description}</p>
                      )}
                      <div className="mt-2 flex items-center space-x-4 text-xs text-gray-500">
                        {task.dueDate && (
                          <div className="flex items-center space-x-1">
                            <CalendarIcon className="w-3 h-3" />
                            <span>Due {new Date(task.dueDate).toLocaleDateString()}</span>
                          </div>
                        )}
                        {task.assignees?.length > 0 && (
                          <div className="flex items-center space-x-1">
                            <UserGroupIcon className="w-3 h-3" />
                            <span>{task.assignees.length} assignee{task.assignees.length > 1 ? 's' : ''}</span>
                          </div>
                        )}
                      </div>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => {
                          setSelectedTask(task);
                          setIsTaskDetailsModalOpen(true);
                        }}
                        className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                        title="View Task Details"
                      >
                        <InformationCircleIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          setEditingTask(task);
                          setIsTaskModalOpen(true);
                        }}
                        className="p-1 text-gray-400 hover:text-green-600 transition-colors"
                        title="Edit Task"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setTaskToDelete(task)}
                        className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                        title="Delete Task"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        </div>

        {/* Recurring Tasks */}
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">
              Recurring Tasks ({recurringTasks.length})
            </h2>
          </div>

          <div className="divide-y divide-gray-200">
            {recurringTasks.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                No recurring tasks yet. Create a recurring task to get started.
              </div>
            ) : (
              recurringTasks.map((template, index) => (
                <motion.div
                  key={template.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                  className="p-6 hover:bg-gray-50 transition-colors"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <ArrowPathIcon className="w-5 h-5 text-blue-500" />
                        <h3 className="text-lg font-medium text-gray-900">{template.title}</h3>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          template.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                        }`}>
                          {template.isActive ? 'Active' : 'Inactive'}
                        </span>
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          template.priority === 'HIGH' ? 'bg-red-100 text-red-800' :
                          template.priority === 'NORMAL' ? 'bg-blue-100 text-blue-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {template.priority}
                        </span>
                      </div>

                      {template.description && (
                        <p className="text-gray-600 mb-2">{template.description}</p>
                      )}

                      <div className="flex items-center space-x-4 text-sm text-gray-500 mb-2">
                        <span>Every {template.recurrenceValue} {template.recurrenceType.toLowerCase()}</span>
                        {template.endDate && (
                          <span>Until {new Date(template.endDate).toLocaleDateString()}</span>
                        )}
                        {template.maxOccurrences && (
                          <span>Max {template.maxOccurrences} occurrences</span>
                        )}
                      </div>

                      <div className="flex items-center space-x-4 text-sm text-gray-500">
                        <span>Created by {template.creator.firstName} {template.creator.lastName}</span>
                        {template.assignees && template.assignees.length > 0 && (
                          <span>Assigned to {template.assignees.length} user(s)</span>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center space-x-2 ml-4">
                      <button
                        onClick={() => handleViewRecurringTaskDetails(template)}
                        className="p-1 text-gray-400 hover:text-green-600 transition-colors"
                        title="View Details"
                      >
                        <EyeIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleEditRecurringTask(template)}
                        className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
                        title="Edit Recurring Task"
                      >
                        <PencilIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDeleteRecurringTask(template)}
                        className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                        title="Delete Recurring Task"
                      >
                        <TrashIcon className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))
            )}
          </div>
        </div>

        {/* Task Modal */}
        <TaskModal
          isOpen={isTaskModalOpen}
          onClose={() => {
            setIsTaskModalOpen(false);
            setEditingTask(null);
          }}
          onSubmit={editingTask ? handleEditTask : handleAddTask}
          task={editingTask}
          teams={teams}
          users={users}
          projects={[project]}
          creatorId={currentUser?.id}
          isLoading={false}
          hideProjectSelection={true}
        />

        {/* Task Details Modal */}
        <TaskDetailsModal
          isOpen={isTaskDetailsModalOpen}
          onClose={() => {
            setIsTaskDetailsModalOpen(false);
            setSelectedTask(null);
          }}
          task={selectedTask}
          onTaskUpdated={() => {
            // Reload project details to get updated task data
            loadProjectDetails();
          }}
        />

        {/* Delete Confirmation */}
        <ConfirmDialog
          isOpen={!!taskToDelete}
          onClose={() => setTaskToDelete(null)}
          onConfirm={handleDeleteTask}
          title="Delete Task"
          message={`Are you sure you want to delete "${taskToDelete?.title}"? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          type="danger"
          isLoading={false}
        />

        {/* Recurring Task Modal */}
        <RecurringTaskModal
          isOpen={isRecurringTaskModalOpen}
          onClose={() => {
            setIsRecurringTaskModalOpen(false);
            setEditingRecurringTask(null);
          }}
          recurringTask={editingRecurringTask}
          onSave={() => {
            loadProjectDetails(); // Reload data after saving
          }}
          teams={teams}
          users={users}
          projects={[project].filter(Boolean)} // Pass current project
        />

        {/* Recurring Task Details Modal */}
        <RecurringTaskDetailsModal
          isOpen={isRecurringTaskDetailsModalOpen}
          onClose={() => {
            setIsRecurringTaskDetailsModalOpen(false);
            setSelectedRecurringTask(null);
          }}
          recurringTask={selectedRecurringTask}
          onEdit={() => {
            setIsRecurringTaskDetailsModalOpen(false);
            handleEditRecurringTask(selectedRecurringTask);
          }}
          onDelete={() => {
            setIsRecurringTaskDetailsModalOpen(false);
            handleDeleteRecurringTask(selectedRecurringTask);
          }}
          currentUser={currentUser}
        />
      </div>
    </Layout>
  );
};

export default ProjectDetails;
